// AddCasePage.tsx
import { Box } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { useGetGenders, useGetRoles } from 'hooks/useLookups';
import { useGetUserHospitals } from 'hooks/useUserInfo';
import { useAuth } from 'context/AuthContext';
import { useEffect, useMemo, useState } from 'react';
import SelectICDs from 'components/modals/SelectICD/SelectICDs';
import dayjs from 'dayjs';
import { CustomFieldType, ImageItem, LabelValueType } from 'types/types/Types';
import { isInterventional, isNeurosurgery } from 'common/caseHelpers';
import SelectCPTs from 'components/modals/SelectCPTs/SelectCPTs';
import CaseNotes from 'components/case/CaseNotes/CaseNotes';
import { useNavigate, useParams } from 'react-router-dom';
import { snackbarService } from 'services/snackbarService';
import useGlobalStyles from 'styles/globalStyles';
import RegistryPatientInformation from './RegistryPatientInformation';
import { RegistryProvider, useRegistryContext } from 'context/RegistryContext';
import { buildDynamicSchema } from 'validation/registryCaseValidation';
import RegistryProcedureDetails from '../RegistryPatientDetailsPage/RegistryProcedureDetails';
import RegistryCustomFields from './RegistryCustomFields';
import { registryProcedureFields } from 'common/constants';
import { RegistryPatientProvider } from 'context/RegistryPatientContext';
import { mapRegistryPatientToForm } from './handleInitialValues';
import { useCreateRegistryPatient, useGetRegistryPatientById, useUpdateRegistryPatient, useUpdateRegistryPatientImages } from 'hooks/useRegistryPatient';
import CaseImages from 'components/case/CaseImages/CaseImages';

const defaultInitialValues = {
  patient_first_name: '',
  patient_last_name: '',
  gender: '',
  date_of_birth: null,
  mrn: '',
  insurance: '',
  institute: undefined,
  diagnosisCodes: [],
  date: dayjs(),
  type: '',
  vendors: '',
  findings: '',
  complications: '',
  outcome: '',
  cptCodes: [],
  notes: [],
};


export default function AddRegistryPatientPage() {
  const { registryId, patientId } = useParams();

  return (
    <RegistryProvider registryId={Number(registryId)}>
      <RegistryPatientProvider patientId={Number(patientId)}>
        <AddRegistryPatientPageWrapper />
      </RegistryPatientProvider>
    </RegistryProvider>
  )
}

function AddRegistryPatientPageWrapper() {
  const { registry } = useRegistryContext();
  const registryId = Number(useParams()?.registryId)
  const patientId = Number(useParams()?.patientId)
  const { user } = useAuth();
  const globalStyles = useGlobalStyles();

  const [images, setImages] = useState<ImageItem[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const navigate = useNavigate();

  const speciality = useMemo(() => {
    return user.user_profile[0].speciality?.name;
  }, [user]);

  const { data: patient } = useGetRegistryPatientById({
    registryId: registryId,
    patientId: patientId,
  }, {
    enabled: !!patientId && !!registry?.id,
    onSuccess: (res: any) => {
      const { data } = res.data;
      reset(mapRegistryPatientToForm(data));
      setImages(res.data.data.images);
    },
    onError: (err: any) => {
      console.error('error', err);
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const validationSchema = useMemo(() => {
    if (!registry || registry.fields == null) {
      return undefined;
    }
    return yupResolver(buildDynamicSchema(registry.fields));
  }, [registry]);

  const methods = useForm<any>({
    resolver: validationSchema,
    defaultValues: defaultInitialValues,
  });

  const {
    reset,
    control,
    setValue,
    formState: { isValid },
  } = methods;

  const {
    handleSubmit,
  } = methods;

  const isMultipleSelection = useMemo(() => {
    return isNeurosurgery(speciality) || isInterventional(speciality);
  }, [speciality]);

  // All Lookups
  const { data: genders } = useGetGenders();
  const { data: userHospitals, refetch: refetchHospitals } = useGetUserHospitals(user.id);
  const { data: roles, refetch: refetchRoles } = useGetRoles();

  const sharedProps = useMemo(() => ({
    genders,
    userHospitals,
    refetchHospitals,
    roles,
  }), [
    genders,
    userHospitals,
    refetchHospitals,
    roles,
  ]);

  const newImages = useMemo(() => {
    return images?.filter(item => !item?.id);
  }, [images]);

  const updateImagesMutation = useUpdateRegistryPatientImages(
    (data: any) => { },
    (error: any) => console.log('error update images', error),
  )

  const updateRegistryPatientImages = async (pId?: number) => {
    await updateImagesMutation.mutateAsync({
      dto: {
        registryId,
        patientId,
      },
      images: newImages,
    });
  }

  const createMutation = useCreateRegistryPatient(
    async (res: any) => {
      try {
        const { data, status_code: statusCode } = res;
        if (statusCode == 200) {
          snackbarService.showSnackbar('Patient has been added successfully', 'success');
          navigate(`/registries/all-registries/${registry.id}/patients/${data.id}`);
        } else {
          snackbarService.showSnackbar(data?.message, 'error');
        }
      } catch (error) {
        snackbarService.showSnackbar(res?.message, 'error');
      }
    },
    () => { },
  )

  const updateMutation = useUpdateRegistryPatient(
    async (res: any) => {
      const { data, status_code: statusCode } = res;
      if (statusCode == 200) {
        if (newImages?.length > 0) {
          await updateRegistryPatientImages(data.id);
        }
        snackbarService.showSnackbar('Patient has been updated successfully', 'success');
        navigate(`/registries/all-registries/${registry.id}/patients/${patientId}`);
      } else {
        snackbarService.showSnackbar(data, 'error');
      }
    },
    () => { },
  )

  const isSubmitting = useMemo(() => {
    return createMutation.isLoading || updateMutation.isLoading || isUploading;
  }, [createMutation.isLoading, updateMutation.isLoading, isUploading]);

  const onSubmit = async (data: any) => {

    const institute = userHospitals?.find(h => h.key == data.institute);
    const instituteWithId = institute && { id: institute.key, label: institute.label, value: institute.value };

    if (patientId) {
      await updateMutation.mutateAsync({
        ...data,
        registryId: registry.id,
        patientId: patientId,
        institute: instituteWithId ?? data.institute,
      });
    } else {
      await createMutation.mutateAsync({
        ...data,
        registryId: registry.id,
        institute: instituteWithId ?? data.institute,
      });
    }
  };

  // const cpts = useWatch({
  //   control,
  //   name: `cpts`,
  // });

  // const cptModifier = useWatch({
  //   control,
  //   name: `cpt_modifier`,
  // });


  useEffect(() => {
    reset(defaultInitialValues);
  }, [patientId, reset]);

  const hasICDs = useMemo(() => {
    if (!registry || registry.fields == null) return false;
    return registry.fields.some((field: CustomFieldType) => field.name == 'diagnosisCodes')
  }, [registry])

  const hasCPTs = useMemo(() => {
    if (!registry || registry.fields == null) return false;
    return registry.fields.some((field: CustomFieldType) => field.name == 'cptCodes')
  }, [registry])

  const hasCustomFields = useMemo(() => {
    if (!registry || registry.fields == null) return false;
    return registry.fields.some((field: CustomFieldType) => !registryProcedureFields.includes(field.name));
  }, [registry])

  const hasNotes = useMemo(() => {
    if (!registry || registry.fields == null) return false;
    return registry.fields.some((field: CustomFieldType) => field.name == 'notes')
  }, [registry])

  const hasImages = useMemo(() => {
    if (!registry || registry.fields == null) return false;
    return registry.fields.some((field: CustomFieldType) => field.name == 'images')
  }, [registry])

  return (
    <Box className={globalStyles.container} p={3}>
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >

          {/* <Box mb={2}>
            <BreadCrumb />
          </Box> */}

          {/* Patient Information */}
          <Box>
            <RegistryPatientInformation shared={sharedProps} />
          </Box>

          {/* Diagnosis */}
          <Box mt={3}>
            {/* <Diagnosis /> */}
          </Box>

          {/* ICD-9/10 */}
          {hasICDs && <Box mt={3}>
            <SelectICDs name={'diagnosisCodes'} title={'Diagnosis (ICD-9/10)'} />
          </Box>}

          {/* Procedure Details */}
          <Box mt={3}>
            <RegistryProcedureDetails shared={sharedProps} />
          </Box>

          {hasCPTs && <Box mt={3}>
            <SelectCPTs
              name={'cptCodes'}
              title={'CPT Codes and RVUs'}
              procedureNamePath={'type'}
            />
          </Box>}

          {hasCustomFields && <Box mt={3}>
            <RegistryCustomFields shared={sharedProps} />
          </Box>}


          {hasNotes && <Box mt={3}>
            <CaseNotes />
          </Box>}

          {hasImages && <Box mt={3}>
            <CaseImages images={images} setImages={setImages} />
          </Box>}

          <Box my={3}>
            <CustomButton
              text={patientId ? "Update Patient" : "Add Patient"}
              fullWidth
              type='submit'
              loading={isSubmitting}
            // disabled={!isValid}
            />
          </Box>
        </Box>

      </FormProvider>
    </Box>
  );
}
